import React, { useState, useEffect } from "react";
import { Progress, Tooltip } from "antd";
import { PlayCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { UploadingFile } from "../../hooks/useFileUpload";
import MediaPlayer from "../media-player/media-player";
import AttachmentCarousel from "../attachment-carousel/attachment-carousel";
import { MediaType } from "../../constants/field-values";
import { useVideoThumbnail } from "hooks/useVideoThumbnail";

interface FileAttachmentInputItemProps {
  file: UploadingFile;
  previewUrl?: string;
  clickable: boolean;
  onItemClick: (file: UploadingFile, index: number) => void;
  onRemoveFile: (fileId: string) => void;
  fileIndex: number;
}

const BROWSER_RENDERABLE_IMAGE = new Set([
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
  'image/webp', 'image/avif', 'image/svg+xml',
]);

const getFileEmoji = (mimeType?: string) => {
  if (mimeType?.startsWith('image/')) return '🖼️';
  if (mimeType?.startsWith('video/')) return '🎥';
  if (mimeType?.startsWith('audio/')) return '🎵';
  if (mimeType === 'application/pdf') return '📄';
  return '📎';
};

const FileAttachmentInputItem: React.FC<FileAttachmentInputItemProps> = ({
  file,
  previewUrl,
  clickable,
  onItemClick,
  onRemoveFile,
  fileIndex,
}) => {
  const { mimeType, status, progress } = file;
  const isVideo = mimeType?.startsWith('video/');
  const isImage = mimeType?.startsWith('image/');

  // Use publicUrl (converted JPEG) when available; only use blob for browser-native formats
  const imageSrc = file.publicUrl || (BROWSER_RENDERABLE_IMAGE.has(mimeType || '') ? previewUrl : null);

  // Use useVideoThumbnail hook to extract first-frame thumbnail from the blob URL
  const videoThumbnail = useVideoThumbnail(isVideo && previewUrl ? previewUrl : null, 1);

  const tooltipTitle = (() => {
    if (status === 'uploading') return 'File uploading...';
    if (status === 'converting') return 'Submitted format not compatible. Converting to accepted format...';
    if (status === 'error') return 'Upload failed';
    if (clickable) {
      const fmt = mimeType?.split('/')[1]?.toUpperCase();
      return `Click to preview${fmt ? ` (${fmt})` : ''}`;
    }
    return undefined;
  })();

  return (
    <Tooltip key={file.id} title={tooltipTitle}>
      <div className="file-attachment-input-item">
        <div
          className={`file-attachment-input-thumbnail${clickable ? ' clickable' : ''}`}
          onClick={() => onItemClick(file, fileIndex)}
        >
          {/* Background: image preview, video thumbnail, or emoji icon */}
          <div className="file-attachment-input-preview">
            {isImage && imageSrc ? (
              <img
                src={imageSrc}
                alt={file.fileName}
                className="file-attachment-input-image"
              />
            ) : isVideo && videoThumbnail ? (
              <img
                src={videoThumbnail}
                alt={file.fileName}
                className="file-attachment-input-image"
              />
            ) : (
              <div className="file-attachment-input-icon">
                {getFileEmoji(mimeType)}
              </div>
            )}
          </div>

          {/* Play overlay for videos */}
          {isVideo && clickable && (
            <div className="file-attachment-input-play-overlay">
              <PlayCircleOutlined style={{ fontSize: 22, color: '#fff' }} />
            </div>
          )}

          {/* Circular upload progress */}
          {status === 'uploading' && (
            <div className="file-attachment-input-progress-overlay">
              <Progress
                type="circle"
                percent={Math.round(progress)}
                size={40}
                strokeColor="#1890ff"
                strokeWidth={6}
                showInfo={false}
              />
            </div>
          )}

          {/* Converting spinner */}
          {status === 'converting' && (
            <div className="file-attachment-input-progress-overlay">
              <SyncOutlined spin style={{ fontSize: 22, color: '#722ed1' }} />
            </div>
          )}

          {/* Error indicator */}
          {status === 'error' && (
            <div className="file-attachment-input-status error">✗</div>
          )}
        </div>

        <div
          onClick={(e) => { e.stopPropagation(); onRemoveFile(file.id); }}
          className="file-attachment-input-remove"
        >
          ✕
        </div>
      </div>
    </Tooltip>
  );
};

interface FileAttachmentInputProps {
  uploadingFiles: UploadingFile[];
  onRemoveFile: (fileId: string) => void;
}

export const FileAttachmentInput: React.FC<FileAttachmentInputProps> = ({
  uploadingFiles,
  onRemoveFile,
}) => {
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [mediaPlayer, setMediaPlayer] = useState<{ src: string; type: MediaType; index: number } | null>(null);

  // Create blob URLs for images and videos when files arrive
  useEffect(() => {
    const newPreviewUrls: Record<string, string> = {};
    const urlsToCleanup: string[] = [];

    uploadingFiles.forEach(file => {
      const isMedia = file.mimeType?.startsWith('image/') || file.mimeType?.startsWith('video/');
      if (isMedia && file.file && !previewUrls[file.id]) {
        newPreviewUrls[file.id] = URL.createObjectURL(file.file);
      } else if (previewUrls[file.id]) {
        newPreviewUrls[file.id] = previewUrls[file.id];
      }
    });

    Object.keys(previewUrls).forEach(fileId => {
      if (!uploadingFiles.find(f => f.id === fileId)) {
        urlsToCleanup.push(previewUrls[fileId]);
      }
    });

    urlsToCleanup.forEach(url => URL.revokeObjectURL(url));
    setPreviewUrls(newPreviewUrls);

    return () => {
      Object.values(newPreviewUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [uploadingFiles]);

  if (uploadingFiles.length === 0) return null;

  const handleItemClick = (file: UploadingFile, index: number) => {
    const src = file.publicUrl || previewUrls[file.id];
    if (!src || file.status === 'uploading' || file.status === 'converting') return;

    const mime = file.mimeType || '';
    if (mime.startsWith('image/')) {
      setMediaPlayer({ src, type: MediaType.Image, index });
    } else if (mime.startsWith('video/')) {
      setMediaPlayer({ src, type: MediaType.Video, index });
    } else if (mime.startsWith('audio/')) {
      setMediaPlayer({ src, type: MediaType.Audio, index });
    } else if (mime === 'application/pdf') {
      setMediaPlayer({ src, type: MediaType.Document, index });
    }
  };

  const isClickable = (file: UploadingFile) =>
    file.status === 'completed' && !!(file.publicUrl || previewUrls[file.id]);

  const mediaSiblings = uploadingFiles
    .filter((f) => isClickable(f))
    .map((f) => {
      const src = f.publicUrl || previewUrls[f.id];
      const mime = f.mimeType || '';
      return {
        src: src || '',
        mediaType: mime.startsWith('image/')
          ? MediaType.Image
          : mime.startsWith('video/')
          ? MediaType.Video
          : mime.startsWith('audio/')
          ? MediaType.Audio
          : MediaType.Document,
      };
    })
    .filter((s) => !!s.src);

  return (
    <>
      <div className="file-attachment-input-container">
        <div className="file-attachment-input-list">
          {uploadingFiles.map((file, idx) => (
            <FileAttachmentInputItem
              key={file.id}
              file={file}
              previewUrl={previewUrls[file.id]}
              clickable={isClickable(file)}
              onItemClick={handleItemClick}
              onRemoveFile={onRemoveFile}
              fileIndex={idx}
            />
          ))}
        </div>
      </div>

      {mediaPlayer && (
        <MediaPlayer
          src={mediaPlayer.src}
          mediaType={mediaPlayer.type}
          open={!!mediaPlayer}
          onCancel={() => setMediaPlayer(null)}
          siblings={mediaSiblings.length > 1 ? mediaSiblings : undefined}
          siblingIndex={mediaPlayer.index}
        />
      )}
    </>
  );
};
